import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { formatRelative } from '../format';
import type { Area, DashboardUser, Level, LocationSummary, Role, TabId, TabLevel, UsersResponse } from '../types';
import { Dialog } from './Dialog';

const ROLE_HELP: Record<Role, string> = {
  owner: 'Everything, including managing users — cannot be limited',
  admin: 'Starts with every tab; can be limited below, but never manages users',
  member: 'Starts with the reports, Sub-accounts and the Assistant; adjust below',
  viewer: 'Never more than View — on tabs and in the Assistant',
};

const TAB_LEVEL_LABEL: Record<TabLevel, string> = { off: 'Off', view: 'View', edit: 'Edit' };

const LEVEL_LABEL: Record<Level, string> = { off: 'Off', view: 'View', act: 'Act' };

interface Draft {
  id: string | null;
  name: string;
  email: string;
  password: string;
  role: Role;
  permissions: Record<Area, Level>;
  tabs: Record<TabId, TabLevel>;
  allLocations: boolean;
  locationIds: string[];
  disabled: boolean;
}

/** Settings → Users & permissions (owners only). */
export function UsersCard({ locations }: { locations: LocationSummary[] }) {
  const [data, setData] = useState<UsersResponse | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.users().then(setData).catch((cause) => setError(cause instanceof ApiError ? cause.message : String(cause)));
  }, []);
  useEffect(load, [load]);

  if (!data) return error ? <div className="banner error">{error}</div> : <p className="empty">Loading…</p>;

  function open(user: DashboardUser | null) {
    if (!data) return;
    setDraft(
      user
        ? {
            id: user.id,
            name: user.name ?? '',
            email: user.email,
            password: '',
            role: user.role,
            permissions: { ...user.permissions },
            tabs: { ...user.tabs },
            allLocations: !user.locationIds,
            locationIds: user.locationIds ?? [],
            disabled: user.disabled,
          }
        : {
            id: null,
            name: '',
            email: '',
            password: '',
            role: 'member',
            permissions: { ...data.defaults.member },
            tabs: { ...data.tabDefaults.member },
            allLocations: true,
            locationIds: [],
            disabled: false,
          },
    );
  }

  const nameOf = (id: string) => locations.find((location) => location.id === id)?.name ?? id;

  return (
    <div className="card">
      <div className="card-head">
        <h2>Users & permissions</h2>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => open(null)}>
          Add user
        </button>
      </div>
      <p className="card-sub">
        Everyone signs in with their own email and password. Each tab is <strong>Off</strong> (hidden),{' '}
        <strong>View</strong>, or <strong>Edit</strong> (view plus that tab’s changes). The Assistant levels decide what
        the AI may do for them in HighLevel — <strong>View</strong> looks things up, <strong>Act</strong> can make changes,
        each one approved in the chat. Limiting someone to some sub-accounts applies to the reports too.
      </p>
      {error ? <div className="banner error">{error}</div> : null}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Person</th>
              <th>Role</th>
              <th>Sub-accounts</th>
              <th>Tabs</th>
              {data.areas.map((area) => (
                <th key={area.id}>{area.label}</th>
              ))}
              <th>Last sign-in</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.users.map((user) => (
              <tr key={user.id} className={user.disabled ? 'muted-row' : undefined}>
                <td>
                  <div className="cell-strong">{user.name || user.email}</div>
                  <div className="cell-sub">
                    {user.email}
                    {user.disabled ? ' · disabled' : ''}
                  </div>
                </td>
                <td>
                  <span className="role-chip">{user.role}</span>
                </td>
                <td className="small">
                  {user.locationIds
                    ? user.locationIds.length <= 2
                      ? user.locationIds.map(nameOf).join(', ') || 'None'
                      : `${user.locationIds.length} sub-accounts`
                    : 'All'}
                </td>
                <td className="small">{describeTabs(user, data)}</td>
                {data.areas.map((area) => (
                  <td key={area.id}>
                    <span className={`level-pill level-pill-${user.role === 'owner' ? 'act' : user.permissions[area.id]}`}>
                      {LEVEL_LABEL[user.role === 'owner' ? 'act' : user.permissions[area.id]]}
                    </span>
                  </td>
                ))}
                <td className="small nowrap">{user.lastLoginAt ? formatRelative(user.lastLoginAt) : 'Never'}</td>
                <td>
                  <button type="button" className="btn btn-sm" onClick={() => open(user)}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
            {data.users.length === 0 ? (
              <tr>
                <td colSpan={6 + data.areas.length} className="empty">
                  No named users yet. Add yourself as an owner first — the setup password stops working once anyone has a login.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {draft ? (
        <UserDialog
          draft={draft}
          data={data}
          locations={locations}
          onClose={() => setDraft(null)}
          onSaved={() => {
            setDraft(null);
            load();
          }}
        />
      ) : null}
    </div>
  );
}

function UserDialog({
  draft: initial,
  data,
  locations,
  onClose,
  onSaved,
}: {
  draft: Draft;
  data: UsersResponse;
  locations: LocationSummary[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(initial);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function update(patch: Partial<Draft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function setRole(role: Role) {
    // A new role starts from that role's defaults; adjust from there.
    update({ role, permissions: { ...data.defaults[role] }, tabs: { ...data.tabDefaults[role] } });
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const body = {
        name: draft.name.trim() || null,
        email: draft.email.trim(),
        role: draft.role,
        permissions: draft.permissions,
        tabs: draft.tabs,
        locationIds: draft.allLocations ? null : draft.locationIds,
        ...(draft.password ? { password: draft.password } : {}),
      };
      if (draft.id) await api.updateUser(draft.id, { ...body, disabled: draft.disabled });
      else await api.createUser({ ...body, name: body.name ?? undefined, password: draft.password });
      onSaved();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!draft.id || !window.confirm(`Delete the login for ${draft.email}?`)) return;
    try {
      await api.deleteUser(draft.id);
      onSaved();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause));
    }
  }

  const shown = locations.filter((location) => location.name.toLowerCase().includes(filter.toLowerCase()));
  const isOwner = draft.role === 'owner';

  return (
    <Dialog title={draft.id ? `Edit ${draft.email}` : 'Add user'} onClose={onClose}>
      <div className="stack">
        <div className="dialog-grid">
          <div className="field">
            <label htmlFor="user-name">Name</label>
            <input id="user-name" type="text" value={draft.name} onChange={(event) => update({ name: event.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="user-email">Email</label>
            <input id="user-email" type="text" inputMode="email" value={draft.email} onChange={(event) => update({ email: event.target.value })} />
          </div>
          <div className="field full">
            <label htmlFor="user-password">{draft.id ? 'New password (leave empty to keep)' : 'Password (10+ characters)'}</label>
            <input
              id="user-password"
              type="password"
              autoComplete="new-password"
              value={draft.password}
              onChange={(event) => update({ password: event.target.value })}
            />
          </div>
        </div>

        <div className="field">
          <span className="field-label">Role</span>
          <div className="segmented" role="group" aria-label="Role" style={{ alignSelf: 'flex-start' }}>
            {data.roles.map((role) => (
              <button type="button" key={role} aria-pressed={draft.role === role} onClick={() => setRole(role)}>
                {role[0]!.toUpperCase() + role.slice(1)}
              </button>
            ))}
          </div>
          <span className="small muted">{ROLE_HELP[draft.role]}</span>
        </div>

        <div className="field">
          <span className="field-label">Tabs they can open</span>
          <table className="perm-grid">
            <tbody>
              {data.tabs.map((tab) => {
                const levels: TabLevel[] = tab.edit ? ['off', 'view', 'edit'] : ['off', 'view'];
                const value = isOwner ? (tab.edit ? 'edit' : 'view') : draft.tabs[tab.id];
                return (
                  <tr key={tab.id}>
                    <td>
                      <div className="cell-strong">{tab.label}</div>
                      <div className="cell-sub">
                        View: {tab.view}.{tab.edit ? ` Edit: ${tab.edit}.` : ''}
                      </div>
                    </td>
                    <td className="nowrap">
                      <div className="segmented" role="group" aria-label={`${tab.label} access`}>
                        {levels.map((level) => (
                          <button
                            type="button"
                            key={level}
                            disabled={isOwner || (draft.role === 'viewer' && level === 'edit')}
                            aria-pressed={value === level}
                            onClick={() => update({ tabs: { ...draft.tabs, [tab.id]: level } })}
                          >
                            {TAB_LEVEL_LABEL[level]}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <span className="small muted">Settings → Users is always owners only.</span>
        </div>

        <div className="field">
          <span className="field-label">What the Assistant may do for them</span>
          <table className="perm-grid">
            <tbody>
              {data.areas.map((area) => (
                <tr key={area.id}>
                  <td>
                    <div className="cell-strong">{area.label}</div>
                    <div className="cell-sub">
                      View: {area.view}. Act: {area.act}.
                    </div>
                  </td>
                  <td className="nowrap">
                    <div className="segmented" role="group" aria-label={`${area.label} level`}>
                      {(['off', 'view', 'act'] as Level[]).map((level) => (
                        <button
                          type="button"
                          key={level}
                          disabled={isOwner || (draft.role === 'viewer' && level === 'act')}
                          aria-pressed={(isOwner ? 'act' : draft.permissions[area.id]) === level}
                          onClick={() => update({ permissions: { ...draft.permissions, [area.id]: level } })}
                        >
                          {LEVEL_LABEL[level]}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {isOwner ? <span className="small muted">Owners can do everything.</span> : null}
        </div>

        <div className="field">
          <span className="field-label">Sub-accounts</span>
          <label className="switch">
            <input type="checkbox" role="switch" checked={draft.allLocations} onChange={(event) => update({ allLocations: event.target.checked })} />
            All sub-accounts, including new ones
          </label>
          {!draft.allLocations ? (
            <div className="location-picker">
              <input type="search" placeholder="Filter sub-accounts…" value={filter} onChange={(event) => setFilter(event.target.value)} />
              <div className="location-list">
                {shown.map((location) => (
                  <label key={location.id} className="scope-row">
                    <input
                      type="checkbox"
                      checked={draft.locationIds.includes(location.id)}
                      onChange={(event) =>
                        update({
                          locationIds: event.target.checked
                            ? [...draft.locationIds, location.id]
                            : draft.locationIds.filter((id) => id !== location.id),
                        })
                      }
                    />
                    {location.name}
                  </label>
                ))}
              </div>
              <span className="small muted">{draft.locationIds.length} selected</span>
            </div>
          ) : null}
        </div>

        {draft.id ? (
          <label className="switch">
            <input type="checkbox" role="switch" checked={draft.disabled} onChange={(event) => update({ disabled: event.target.checked })} />
            Disabled — cannot sign in
          </label>
        ) : null}

        {error ? <div className="inline-result bad">{error}</div> : null}
        <div className="dialog-actions">
          {draft.id ? (
            <button type="button" className="btn btn-danger" style={{ marginRight: 'auto' }} onClick={remove}>
              Delete
            </button>
          ) : null}
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !draft.email.trim() || (!draft.id && draft.password.length < 10)}
            onClick={save}
          >
            {busy ? 'Saving…' : draft.id ? 'Save' : 'Add user'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

/** A short line for the table: which tabs, and how many with edit. */
function describeTabs(user: DashboardUser, data: UsersResponse): string {
  if (user.role === 'owner') return 'All · edit';
  const open = data.tabs.filter((tab) => user.tabs[tab.id] !== 'off');
  const editing = open.filter((tab) => user.tabs[tab.id] === 'edit').length;
  if (open.length === 0) return 'None';
  const names = open.length === data.tabs.length ? 'All' : open.map((tab) => tab.label).join(', ');
  return editing ? `${names} · ${editing} with edit` : `${names} · view only`;
}
